/* eslint-disable import/no-default-export */
import Backbone from 'app/common/vendor/backbone';

import API from 'app/gamma/src/api';
import { normalizeBoard } from 'app/gamma/src/api/normalizers/board';
import { normalizeTeam } from 'app/gamma/src/api/normalizers/team';

import { closePopovers } from 'app/gamma/src/modules/state/ui/popovers';
import { TeamModel } from 'app/gamma/src/types/models';
import { Action, actionCreator } from 'app/gamma/src/util/actions';
import { createReducer } from 'app/gamma/src/util/reducers';
import { isTeamBuilderPhaseTwoExperiment } from 'app/gamma/src/selectors/create-menu';

// Action types
import {
  LOAD_BOARD_SUCCESS,
  LOAD_HEADER_SUCCESS,
  LOAD_MEMBER_ACTIVITY_SUCCESS,
  LOAD_MY_BOARDS_SUCCESS,
  LOAD_TEAM_SUCCESS,
  LoadBoardSuccessAction,
  LoadHeaderSuccessAction,
  LoadMemberActivitySuccessAction,
  LoadMyBoardsSuccessAction,
  LoadTeamSuccessAction,
} from 'app/gamma/src/modules/loaders';

import { Noun, Verb, trackUe } from '@trello/analytics';

import {
  SOCKET_ORGANIZATION,
  SocketOrganizationAction,
} from 'app/gamma/src/modules/sockets';

import {
  SET_BOARD_CLOSED,
  SetBoardClosedAction,
} from 'app/gamma/src/modules/state/models/boards';
import {
  BoardResponse,
  LimitStatus,
  OrganizationsResponse,
  StandardThunkAction,
} from 'app/gamma/src/types';
import { HasRequiredKeys } from 'app/gamma/src/util/types';
import updateModelList from './util/updateModelList';

export const SET_TEAM_DETAILS = Symbol('models/SET_TEAM_DETAILS');
export const CREATE_TEAM_SUCCESS = Symbol('models/CREATE_TEAM_SUCCESS');
export const CREATE_TEAM_FAILURE = Symbol('models/CREATE_TEAM_FAILURE');

export enum TeamType {
  Default = 'default',
  Business = 'business',
  Enterprise = 'enterprise',
}

export type SetTeamDetailsSuccessAction = Action<
  typeof SET_TEAM_DETAILS,
  OrganizationsResponse
>;
export type CreateTeamSuccessAction = Action<
  typeof CREATE_TEAM_SUCCESS,
  OrganizationsResponse
>;

export type TeamState = TeamModel[];

// Reducer
const initialState: TeamState = [];

const getOrganizationsFromBoards = (boards: BoardResponse[]) => {
  return boards
    .filter(
      (board): board is HasRequiredKeys<BoardResponse, 'organization'> =>
        !!board.organization,
    )
    .map((board) => board.organization);
};

const reduceOrganizationsPayload = (
  state: TeamModel[],
  organizations?: OrganizationsResponse | OrganizationsResponse[],
  boards?: BoardResponse[],
) => {
  if (!organizations) {
    return state;
  }

  // seed the state map with the current state
  const stateMap = new Map<string, TeamModel>(
    state.map<[string, TeamModel]>((team) => [team.id, team]),
  );

  // include any organizations that come from boards
  // NOTE: dropping these in first in case the organizations response
  //       has more fully populated organization properties
  if (boards) {
    getOrganizationsFromBoards(boards).forEach((organization) => {
      stateMap.set(
        organization.id,
        normalizeTeam(organization, stateMap.get(organization.id)),
      );
    });
  }

  // if it's not an array of orgs, we are either modifying or adding one team
  if (!Array.isArray(organizations)) {
    stateMap.set(
      organizations.id,
      normalizeTeam(organizations, stateMap.get(organizations.id)),
    );
  } else {
    // now include the organizations from the org response array
    organizations.forEach((organization) => {
      stateMap.set(
        organization.id,
        normalizeTeam(organization, stateMap.get(organization.id)),
      );
    });
  }

  return [...stateMap.values()];
};

const incrementFreeBoardLimitCount = (team: TeamModel): TeamModel => {
  if (
    !team ||
    !team.limits ||
    !team.limits.orgs.freeBoardsPerOrg.count ||
    closed
  ) {
    return team;
  }

  const { count, disableAt, warnAt } = team.limits.orgs.freeBoardsPerOrg;
  const newCount = count + 1;

  let newStatus: LimitStatus = 'ok';
  if (newCount > disableAt) {
    newStatus = 'maxExceeded';
  } else if (newCount === disableAt) {
    newStatus = 'disabled';
  } else if (newCount >= warnAt) {
    newStatus = 'warn';
  }

  return {
    ...team,
    limits: {
      ...team.limits,
      orgs: {
        ...team.limits.orgs,
        freeBoardsPerOrg: {
          ...team.limits.orgs.freeBoardsPerOrg,
          count: newCount,
          status: newStatus,
        },
      },
    },
  };
};

export default createReducer(initialState, {
  [LOAD_MY_BOARDS_SUCCESS](state, { payload }: LoadMyBoardsSuccessAction) {
    const organizations = reduceOrganizationsPayload(
      state,
      payload.organizations,
      payload.boards,
    );

    return organizations;
  },

  [LOAD_BOARD_SUCCESS](state, { payload }: LoadBoardSuccessAction) {
    return reduceOrganizationsPayload(state, payload.organization);
  },

  [LOAD_MEMBER_ACTIVITY_SUCCESS](
    state,
    { payload }: LoadMemberActivitySuccessAction,
  ) {
    return reduceOrganizationsPayload(state, payload.organizations);
  },

  [LOAD_TEAM_SUCCESS](state, { payload }: LoadTeamSuccessAction) {
    return reduceOrganizationsPayload(state, payload);
  },

  [LOAD_HEADER_SUCCESS](state, { payload }: LoadHeaderSuccessAction) {
    return reduceOrganizationsPayload(state, payload.organizations);
  },

  [SET_TEAM_DETAILS](state, { payload }: SetTeamDetailsSuccessAction) {
    return state.map(
      (team) =>
        team.name !== payload.name
          ? team
          : {
              ...team,
              ...normalizeTeam(payload),
            },
    );
  },

  [CREATE_TEAM_SUCCESS](state, { payload }: CreateTeamSuccessAction) {
    return updateModelList(state, payload, normalizeTeam);
  },

  [CREATE_TEAM_FAILURE](state, { message }: Error) {
    // TODO: handle this failure

    return state;
  },
  [SET_BOARD_CLOSED](state, { payload }: SetBoardClosedAction) {
    const { closed, idTeam } = normalizeBoard(payload);

    return state.map(
      (team) =>
        !closed && team.id === idTeam
          ? incrementFreeBoardLimitCount(team)
          : team,
    );
  },

  [SOCKET_ORGANIZATION](state, { payload }: SocketOrganizationAction) {
    return updateModelList(state, payload, normalizeTeam);
  },
});

interface SetTeamDetailsRequest {
  teamName: string;
  name: string;
  displayName: string;
  desc: string;
  website: string;
  'prefs/associatedDomain': string;
  'prefs/googleAppsVersion': 1 | 2;
  'prefs/boardVisibilityRestrict/org': 'admin' | 'none' | 'org';
  'prefs/boardVisibilityRestrict/private': 'admin' | 'none' | 'org';
  'prefs/boardVisibilityRestrict/public': 'admin' | 'none' | 'org';
  'prefs/boardVisibilityRestrict/enterprise': 'admin' | 'none' | 'org';
  'prefs/boardInviteRestrict': 'any' | 'org' | 'managed' | 'org-or-managed';
  'prefs/orgInviteRestrict': string;
  'prefs/permissionLevel': 'private' | 'public';
}

interface CreateTeamRequest {
  type: TeamType;
  displayName: string;
  desc?: string;
  enterprise?: string;
}

interface CreateTeamSuccess {
  type: TeamType;
  team: OrganizationsResponse;
}

// Action creators
export const setTeamDetails = ({
  teamName,
  ...details
}: SetTeamDetailsRequest): StandardThunkAction => {
  return async (dispatch, getState) => {
    try {
      const team = await API.client.rest.put<OrganizationsResponse>(
        `organizations/${teamName}`,
        {
          body: details,
        },
      );

      dispatch(actionCreator(SET_TEAM_DETAILS)(team));

      trackUe({
        category: Noun.TEAM,
        verb: Verb.UPDATES,
        directObj: Noun.PROFILE,
      });
    } catch (err) {
      console.error(err);
    }
  };
};

export const onSubmitCreateTeamSuccess = ({
  type,
  team,
}: CreateTeamSuccess): StandardThunkAction => {
  return async (dispatch) => {
    const { name } = team;
    dispatch(closePopovers());
    switch (type) {
      case TeamType.Business:
        Backbone.history.navigate(`/${name}/billing`, { trigger: true });
        break;
      case TeamType.Enterprise:
        Backbone.history.navigate(`/${name}/account`, { trigger: true });
        break;
      default:
        Backbone.history.navigate(`/${name}`, { trigger: true });
    }
  };
};

export const submitCreateTeam = ({
  type,
  ...details
}: CreateTeamRequest): StandardThunkAction<
  Promise<OrganizationsResponse | void>
> => {
  return async (dispatch) => {
    const inTeamBuilderPhaseTwoExperiment = isTeamBuilderPhaseTwoExperiment();

    try {
      const team = await API.client.rest.post<OrganizationsResponse>(
        'organizations',
        {
          body: details,
        },
      );
      dispatch(actionCreator(CREATE_TEAM_SUCCESS)(team));
      if (!inTeamBuilderPhaseTwoExperiment) {
        dispatch(onSubmitCreateTeamSuccess({ type, team }));
      }

      trackUe({
        category: Noun.HEADER,
        verb: Verb.ADDS,
        directObj: Noun.TEAM,
        method: 'By clicking create team in header add menu',
        context: {
          teamId: team.id,
          teamType: type,
        },
      });

      return team;
    } catch (err) {
      dispatch(actionCreator(CREATE_TEAM_FAILURE)(err));
    }
  };
};
